import { useEffect, useState } from 'react';
import { Platform } from 'react-native';

/**
 * Whether a focus ring should be drawn right now — the behaviour CSS gets from
 * `:focus-visible`, which react-native-web's inline styles cannot express.
 *
 * A ring on every `onFocus` means clicking a control outlines it, which reads
 * as a rendering fault rather than a focus cue. Suppressing the ring entirely
 * is the usual "fix" and it strands keyboard users, who have nothing else
 * telling them where they are.
 *
 * So the modality is tracked instead: the last input decides. A key press
 * turns rings on, a pointer press turns them off, and the answer is global
 * because focus is.
 */
let keyboardModality = false;
const subscribers = new Set<(value: boolean) => void>();
let listening = false;

function setModality(next: boolean) {
  if (keyboardModality === next) return;
  keyboardModality = next;
  for (const notify of subscribers) notify(next);
}

function startListening() {
  if (listening || Platform.OS !== 'web') return;

  const view = globalThis as unknown as {
    addEventListener?: (type: string, handler: (event: unknown) => void, opts?: unknown) => void;
  };
  if (!view.addEventListener) return;

  listening = true;
  // Capture phase: the modality must be known before the focus handler that
  // reads it runs.
  view.addEventListener(
    'keydown',
    (event: unknown) => {
      // Modifier-only presses are not navigation, and treating them as such
      // would flash rings while someone holds a shortcut.
      const key = (event as { key?: string })?.key;
      if (key === 'Shift' || key === 'Control' || key === 'Alt' || key === 'Meta') return;
      setModality(true);
    },
    true,
  );
  view.addEventListener('pointerdown', () => setModality(false), true);
  view.addEventListener('mousedown', () => setModality(false), true);
  view.addEventListener('touchstart', () => setModality(false), true);
}

export function useFocusVisible(): boolean {
  const [visible, setVisible] = useState(keyboardModality);

  useEffect(() => {
    startListening();
    subscribers.add(setVisible);
    setVisible(keyboardModality);
    return () => {
      subscribers.delete(setVisible);
    };
  }, []);

  // Native has no pointer hover or visible focus ring to manage; the platform
  // draws its own focus affordances.
  return Platform.OS === 'web' ? visible : false;
}
